import React from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  BellRing,
  CalendarClock,
  CircleDollarSign,
  FileText,
  Info,
  Lightbulb,
  MessageSquare,
  Target,
  Trash2,
  Users,
} from "lucide-react";
import Loader from "@/shared/components/ui/Loaders/Loader";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import {
  useDismissFollowUpSuggestion,
  useFetchAccountDashboard,
  useFollowUpAlert,
} from "@/features/accounts/hooks/useAccounts";
import { getFollowUpReviewStatus } from "@/features/dashboard/utils/accountsDashboardUtils";

function formatDate(value) {
  if (!value) return "No date";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return "No date";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatDueDate(item) {
  if (!item.due_at) return "No due date";
  if (item.source === "account_action") return formatDate(item.due_date);
  const date = new Date(item.due_at);
  if (Number.isNaN(date.getTime())) return "No due date";
  return new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  }).format(date);
}

function timeAgo(value) {
  if (!value) return "No activity recorded";
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return "No activity recorded";
  const days = Math.max(0, Math.floor((Date.now() - time) / 86400000));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  return formatDate(value);
}

function formatMoney(amount, currency) {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `${currency} ${Number(amount).toLocaleString("en-US")}`;
  }
}

function firstInitials(name) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  return words.length
    ? words.slice(0, 2).map((word) => word[0]).join("").toUpperCase()
    : "AC";
}

const Panel = ({ title, icon: Icon, to, linkLabel, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
    <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
      <h3 className="flex min-w-0 items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
        <Icon size={16} strokeWidth={1.8} className="shrink-0 text-[#207C97]" />
        {title}
      </h3>
      {to && (
        <Link to={to} className="flex shrink-0 items-center gap-1 text-xs font-semibold text-[#207C97] hover:underline">
          {linkLabel} <ArrowRight size={13} />
        </Link>
      )}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

const MetricCard = ({ label, value, detail, icon: Icon }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[0.68rem] font-semibold uppercase text-gray-500 dark:text-gray-400">{label}</p>
        <p className="mt-2 truncate text-2xl font-bold text-gray-900 dark:text-gray-100" title={String(value)}>{value}</p>
      </div>
      <Icon size={19} className="shrink-0 text-[#207C97]" />
    </div>
    <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">{detail}</p>
  </div>
);

const EmptyLine = ({ children }) => (
  <p className="rounded-lg border border-dashed border-gray-200 p-4 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
    {children}
  </p>
);

const QuickAction = ({ to, icon: Icon, title, detail }) => (
  <Link
    to={to}
    className="group flex min-w-0 items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 transition-colors hover:border-[#207C9766] hover:bg-[#207C9708] dark:border-gray-800 dark:bg-gray-900 dark:hover:border-secondary/50 dark:hover:bg-secondary/10"
  >
    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-[#207C971A] text-[#207C97]">
      <Icon size={16} strokeWidth={1.8} />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-xs font-semibold text-gray-800 dark:text-gray-100">
        {title}
      </span>
      <span className="block truncate text-[0.68rem] text-gray-500 dark:text-gray-400">
        {detail}
      </span>
    </span>
    <ArrowRight
      size={14}
      className="shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-[#207C97]"
    />
  </Link>
);

const HEALTH_STATUS_CLASSES = {
  High: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  "Low risk": "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  Medium: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  Watch: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  Low: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
  "Not available": "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
};

const HealthStatus = ({ label }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.68rem] font-semibold ${HEALTH_STATUS_CLASSES[label] || HEALTH_STATUS_CLASSES["Not available"]}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {label}
  </span>
);

const RelationshipHealthPanel = ({ health }) => {
  const score = health?.score;
  const scoreLabel = score == null ? "No data" : `${score}`;
  const ringStyle = score == null
    ? { background: "#e5e7eb" }
    : { background: `conic-gradient(#10b981 ${score * 3.6}deg, #e5e7eb ${score * 3.6}deg)` };

  return (
    <Panel title="Relationship health breakdown" icon={Activity}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex shrink-0 items-center justify-center">
          <div className="grid h-28 w-28 place-items-center rounded-full p-2" style={ringStyle}>
            <div className="grid h-full w-full place-items-center rounded-full bg-white dark:bg-gray-900">
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{scoreLabel}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{health?.label || "No data"}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="min-w-0 flex-1 divide-y divide-gray-100 dark:divide-gray-800">
          {(health?.signals || []).map((signal) => (
            <div key={signal.key} className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <p className="truncate text-sm text-gray-700 dark:text-gray-200">{signal.label}</p>
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">{signal.detail}</p>
              </div>
              <HealthStatus label={signal.status} />
            </div>
          ))}
        </div>
      </div>
      <p className="mt-3 border-t border-gray-100 pt-3 text-[0.68rem] text-gray-500 dark:border-gray-800 dark:text-gray-400">
        {health?.basis || "Calculated from available account signals."}
      </p>
    </Panel>
  );
};

const SUGGESTION_SOURCE_LABELS = {
  email: "Email",
  meeting: "Meeting",
  transcript: "Meeting transcript",
  voice_call: "Voice call",
  linkedin: "LinkedIn",
  whatsapp: "WhatsApp",
};

const SUGGESTION_PRIORITIES = {
  1: { label: "High priority", className: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300" },
  2: { label: "Priority", className: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" },
  3: { label: "Low priority", className: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" },
};

const SuggestedFollowUpsPanel = ({ companyName, suggestions }) => {
  const { data: alert } = useFollowUpAlert({ companyName });
  const { mutate: dismiss, isPending, variables } = useDismissFollowUpSuggestion();
  const reviewStatus = getFollowUpReviewStatus(alert);

  return (
    <Panel title="Suggested follow-ups" icon={BellRing}>
      {suggestions.length ? (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {suggestions.map((item) => {
            const priority = SUGGESTION_PRIORITIES[item.priority] || SUGGESTION_PRIORITIES[2];
            const isDeleting = isPending && variables?.suggestionId === item.id;
            return (
              <div key={item.id} className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <p className="truncate text-xs font-semibold text-gray-800 dark:text-gray-100">{item.title}</p>
                    {item.detail && (
                      <CustomCommonTooltip content={item.detail}>
                        <Info size={13} strokeWidth={1.8} className="shrink-0 cursor-pointer text-gray-400 dark:text-gray-500" />
                      </CustomCommonTooltip>
                    )}
                  </div>
                  <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
                    {[
                      item.contact_name,
                      SUGGESTION_SOURCE_LABELS[item.source_type],
                      item.source_at ? formatDate(item.source_at) : null,
                    ].filter(Boolean).join(" · ") || `Suggested ${formatDate(item.created_at)}`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <span className={`rounded-full px-2 py-1 text-[0.65rem] font-semibold ${priority.className}`}>
                    {priority.label}
                  </span>
                  <button
                    type="button"
                    onClick={() => dismiss({ suggestionId: item.id })}
                    disabled={isDeleting}
                    aria-label={`Delete suggestion: ${item.title}`}
                    title="Delete suggestion"
                    className="rounded-md p-1 text-gray-400 transition-colors hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyLine>
          {alert?.enabled
            ? "No pending follow-ups right now."
            : "Turn on Follow-up alerts at the top right to get AI follow-up suggestions from this account's emails, meetings and calls every 4 days."}
        </EmptyLine>
      )}
      {reviewStatus && (
        <p className="mt-3 border-t border-gray-100 pt-3 text-[0.68rem] text-gray-500 dark:border-gray-800 dark:text-gray-400">
          {reviewStatus}
        </p>
      )}
    </Panel>
  );
};

const PIPELINE_COLORS = ["#10b981", "#207c97", "#f59e0b", "#8b5cf6"];

function buildMilestones(deals, actions) {
  const dealMilestones = (deals?.open_deals || [])
    .filter((deal) => deal.estimated_close_date)
    .map((deal) => ({
      date: deal.estimated_close_date,
      title: deal.next_step || `${deal.name} estimated close`,
      detail: deal.next_step ? `${deal.name} · ${deal.probability}% probability` : deal.name,
      status: new Date(`${deal.estimated_close_date}T23:59:59`).getTime() < Date.now() ? "Overdue" : "Forecast",
    }));
  const actionMilestones = (actions || [])
    .filter((item) => item.due_at || item.due_date)
    .map((item) => ({
      date: item.due_date || item.due_at,
      title: item.title,
      detail: item.stakeholder_name || item.owner || "Account action",
      status: item.overdue ? "Overdue" : item.due_now ? "Action due" : "Scheduled",
    }));

  return [...dealMilestones, ...actionMilestones]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 4);
}

const OpportunityPanel = ({ deals, actions, to }) => {
  const milestones = buildMilestones(deals, actions);
  const totalLabel = deals?.by_currency?.length
    ? deals.by_currency.map(({ currency, amount }) => formatMoney(amount, currency)).join(" · ")
    : "No value recorded";

  return (
    <Panel title="Open opportunities" icon={CircleDollarSign} to={to} linkLabel="See deals">
      {!deals?.available ? (
        <EmptyLine>Deal data is unavailable for this account.</EmptyLine>
      ) : deals.open_count ? (
        <div className="space-y-4 text-xs text-gray-700 dark:text-gray-300">
          <div className="flex items-center justify-between gap-3">
            <p className="font-semibold">{deals.open_count} open {deals.open_count === 1 ? "opportunity" : "opportunities"}</p>
            <p className="font-semibold text-[#207C97]">{totalLabel}</p>
          </div>

          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {(deals.open_deals || []).slice(0, 3).map((deal) => (
              <div key={deal.id} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#207C97]" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-gray-800 dark:text-gray-100">{deal.name}</p>
                  <p className="truncate text-gray-500">{deal.pipeline} · {deal.probability}% probability</p>
                </div>
                <span className="shrink-0 font-semibold">{formatMoney(deal.amount, deal.currency)}</span>
              </div>
            ))}
          </div>

          {deals.by_pipeline?.length ? (
            <div className="border-t border-gray-100 pt-3 dark:border-gray-800">
              <p className="mb-2 text-[0.68rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Open deals by pipeline category</p>
              <div className="space-y-2">
                {deals.by_pipeline.map((item, index) => (
                  <div key={item.pipeline} className="flex items-center gap-2">
                    <span className="w-24 shrink-0 truncate text-gray-500" title={item.pipeline}>{item.pipeline}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                      <div className="h-full rounded-full" style={{ width: `${item.share_percent}%`, backgroundColor: PIPELINE_COLORS[index % PIPELINE_COLORS.length] }} />
                    </div>
                    <span className="w-8 text-right text-gray-500">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {milestones.length ? (
            <div className="border-t border-gray-100 pt-3 dark:border-gray-800">
              <p className="mb-2 text-[0.68rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Key milestones</p>
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {milestones.map((milestone, index) => (
                  <div key={`${milestone.date}-${milestone.title}-${index}`} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${milestone.status === "Overdue" ? "bg-error-500" : "bg-[#207C97]"}`} />
                    <span className="w-20 shrink-0 text-gray-500">{formatDate(milestone.date)}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-gray-700 dark:text-gray-200">{milestone.title}</p>
                      <p className="truncate text-gray-500">{milestone.detail}</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-1 text-[0.65rem] font-semibold ${milestone.status === "Overdue" ? "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300" : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"}`}>
                      {milestone.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-800">No close dates or scheduled milestones recorded.</p>
          )}
        </div>
      ) : (
        <EmptyLine>No open opportunities recorded. Add one when there is a qualified opportunity.</EmptyLine>
      )}
    </Panel>
  );
};

const AccountDashboard = ({ companyName, companyIdentifier }) => {
  const { data, isLoading, isError } = useFetchAccountDashboard({
    companyName,
    enabled: !!companyName,
  });

  if (isLoading) return <Loader text="Loading account dashboard..." />;
  if (isError) {
    return <div className="rounded-lg border border-error-100 bg-error-50 p-4 text-sm text-error-500">Failed to load account dashboard.</div>;
  }

  const account = data?.account || {};
  const actions = data?.next_actions || [];
  const actionSummary = data?.action_summary || {};
  const engagement = data?.engagement_summary || {};
  const activities = data?.recent_activity || [];
  const deals = data?.deal_summary || {};
  const relationshipHealth = data?.relationship_health || {};
  const stakeholders = data?.stakeholders || [];
  const stakeholderDepth = account.stakeholder_depth || {};
  const whitespace = data?.whitespace || [];
  const whitespaceSummary = data?.whitespace_summary || {};
  const insight = data?.conversation_insight;
  const base = `/accounts/${encodeURIComponent(companyIdentifier || companyName)}`;

  return (
    <div className="flex w-full flex-col gap-4 font-inter">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Account snapshot · Engagement means recorded meetings and replies; counts use the last 30 days.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Actions due"
          value={actionSummary.available ? actionSummary.due_now_count : "—"}
          detail={actionSummary.available
            ? `${actionSummary.overdue_count} overdue · ${actionSummary.pending_count} unfinished`
            : "Action sources unavailable"}
          icon={CalendarClock}
        />
        <MetricCard
          label="Last conversation"
          value={engagement.last_at ? timeAgo(engagement.last_at) : "None recorded"}
          detail={`${engagement.last_30_day_count || 0} recorded meetings and replies in 30 days`}
          icon={MessageSquare}
        />
        <MetricCard
          label="Open deals"
          value={deals.available ? deals.open_count : "—"}
          detail={deals.available
            ? `${deals.missing_next_step_count} without a next step`
            : "Deal data unavailable for this account"}
          icon={CircleDollarSign}
        />
        <MetricCard
          label="Marked engaged"
          value={stakeholderDepth.total ? `${stakeholderDepth.engaged}/${stakeholderDepth.total}` : "None mapped"}
          detail={`${stakeholderDepth.at_risk || 0} with recorded risk signals`}
          icon={Users}
        />
      </div>

      <SuggestedFollowUpsPanel
        companyName={companyName}
        suggestions={data?.suggested_follow_ups || []}
      />

      <section aria-label="Account quick actions">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Quick actions
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <QuickAction
            to={`${base}/accountPlan`}
            icon={Target}
            title="Update account plan"
            detail="Add, assign, or complete an action"
          />
          <QuickAction
            to={`${base}/relationship`}
            icon={CalendarClock}
            title="Schedule follow-up"
            detail="Choose a stakeholder and reminder"
          />
          <QuickAction
            to={`${base}/deals`}
            icon={CircleDollarSign}
            title="Manage deals"
            detail="Create or update an opportunity"
          />
          <QuickAction
            to={`${base}/conversations`}
            icon={MessageSquare}
            title="Review conversations"
            detail="Read activity and sync interactions"
          />
        </div>
      </section>

      <RelationshipHealthPanel health={relationshipHealth} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel title="Actions to take" icon={Target}>
          {actions.length ? (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {actions.map((item) => (
                <div key={`${item.source}-${item.id}`} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">{item.title}</p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {[item.stakeholder_name, item.owner, item.description].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <span className={`shrink-0 text-right text-xs font-semibold ${item.overdue ? "text-error-500" : "text-gray-500"}`}>
                      {item.overdue ? "Overdue · " : item.due_now ? "Due today · " : ""}{formatDueDate(item)}
                    </span>
                  </div>
                  <Link
                    to={`${base}/${item.source === "follow_up" ? "relationship" : "accountPlan"}`}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#207C97] hover:underline"
                  >
                    Open {item.source === "follow_up" ? "follow-up" : "action"} <ArrowRight size={12} />
                  </Link>
                </div>
              ))}
              {actionSummary.pending_count > actions.length && (
                <p className="pt-3 text-xs text-gray-500">{actionSummary.pending_count - actions.length} more unfinished actions</p>
              )}
            </div>
          ) : (
            <EmptyLine>{actionSummary.available ? "No unfinished actions recorded. Add an action in Account Plan." : "Action sources are unavailable."}</EmptyLine>
          )}
        </Panel>

        <Panel title="Recent conversations" icon={MessageSquare} to={`${base}/conversations`} linkLabel="See conversations">
          {activities.length ? (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {activities.map((item) => (
                <div key={`${item.type}-${item.id}`} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <span className="rounded-md bg-[#207C971A] px-2 py-1 text-[0.65rem] font-semibold text-[#207C97]">
                    {item.type === "reply" ? "Reply" : "Meeting"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-gray-800 dark:text-gray-100" title={item.subject}>{item.subject}</p>
                    <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
                      {[item.contact_name, item.campaign_name].filter(Boolean).join(" · ") || "Account activity"}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-gray-500" title={formatDate(item.occurred_at)}>{timeAgo(item.occurred_at)}</span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyLine>No meetings or replies recorded for this account. Other activity is available in Conversations.</EmptyLine>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <OpportunityPanel deals={deals} actions={actions} to={`${base}/deals`} />

        <Panel title="Stakeholders" icon={Users} to={`${base}/relationship`} linkLabel="See relationships">
          {stakeholders.length ? (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              <p className="pb-2 text-xs text-gray-500">{stakeholderDepth.total} mapped · {stakeholderDepth.engaged} marked engaged · {stakeholderDepth.at_risk} with recorded risks</p>
              {stakeholders.map((person) => (
                <div key={person.id} className="flex items-center gap-3 py-2">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#207C971A] text-xs font-bold text-[#207C97]">
                    {firstInitials(person.contact_name)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-gray-800 dark:text-gray-100">{person.contact_name}</p>
                    <p className="truncate text-xs text-gray-500">{[person.designation, person.role_authority].filter(Boolean).join(" · ") || "Role not recorded"}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <StatusChip label={person.user_relationship_status || "Not labeled"} variant="neutral" className="px-2 py-0 text-[0.65rem]" />
                    {person.has_recorded_risk && <p className="mt-1 text-[0.65rem] text-error-500">Recorded risk</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyLine>No stakeholders mapped yet. Add relationships to see account coverage.</EmptyLine>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel title="Whitespace audit" icon={Lightbulb} to={`${base}/whitespaceAudit`} linkLabel="See audit">
          {whitespace.length ? (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              <p className="pb-2 text-xs text-gray-500">
                {whitespaceSummary.total_count} audit opportunities · {whitespaceSummary.high_count} marked high
                {whitespaceSummary.last_updated_at ? ` · Updated ${formatDate(whitespaceSummary.last_updated_at)}` : ""}
              </p>
              {whitespace.slice(0, 3).map((item) => (
                <div key={item.id} className="py-2">
                  <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">{item.title}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-gray-500">{item.pain_point || item.score_reason || item.action || "No detail recorded"}</p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyLine>{whitespaceSummary.available ? "No whitespace audit recorded for this account." : "Whitespace audit data is unavailable."}</EmptyLine>
          )}
        </Panel>

        <Panel title="Conversation insight" icon={FileText} to={`${base}/conversations`} linkLabel="See conversations">
          {insight ? (
            <div className="space-y-2 text-xs text-gray-600 dark:text-gray-300">
              <p className="line-clamp-5 leading-5">{insight.summary || "No summary available."}</p>
              {insight.sentiment?.label && <p>Recorded sentiment: {insight.sentiment.label}</p>}
              <p className="text-gray-500">Generated {insight.generated_at ? formatDate(insight.generated_at) : "date unknown"} from {insight.interaction_count} interactions</p>
            </div>
          ) : (
            <EmptyLine>No conversation insight has been generated for this account.</EmptyLine>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default AccountDashboard;
